import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Slide,
  Snackbar,
  Stack,
  Typography,
  Zoom,
} from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { makeStyles } from "@mui/styles";
import { v1 as uuidv1 } from "uuid";
import { styled } from "@mui/system";
import { red } from "@mui/material/colors";

//... Library imports
import VideoSnapshot from "video-snapshot";

//... Icons imports
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import VideocamIcon from "@mui/icons-material/Videocam";
import ImageIcon from "@mui/icons-material/Image";
import novideoIcon from "../../../images/videoIcon.png";
import placeholder from "../../../images/video-placeholder.png";
import InterpreterModeIcon from "@mui/icons-material/InterpreterMode";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

//... Azure services.
import {
  Video_baseUrl,
  uploadAnthrosFileToBlob,
  uploadVideoToBlobWithProgress,
} from "../../../services/AzureStorage";

//... Shared components.
import Progressbar from "../../../sharedComponents/uploadFile/progressbar";
import TitleTooltip from "../../../sharedComponents/Tooltip/TitleTooltip";
import AppDataContext from "../../../common/appContext";
import { Role } from "../../../helper";

import { PlayerProfileService } from "../../../services/playerProfile.Service";
import ImageMagnifier from "../../../sharedComponents/ImageMagnifier/ImageMagnifier";

const useStyles = makeStyles((theme) => ({
  fileInput: {
    display: "flex",
    flex: "1",
    alignItems: "center",
  },
  contentStreamStyles: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    height: "100%",
    maxHeight: "auto",
    // minHeight: "320px",
  },
  mediaStyles: {
    objectFit: "cover",
    boxShadow:
      "0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)",
    border: "1px solid #808080",
    transition: "transform 0.3s",
    transformOrigin: "50% 50%",
    width: "100%",
    height: "auto",
  },
  videoMediaStyles: {
    minWidth: "100%", // Set the width of the media (video or image) to 100%
    minHeight: "100%", // Set the height of the media (video or image) to 100%
    maxHeight: "100%",
    boxShadow:
      "0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)",
    objectFit: "cover",
    border: "1px solid #808080",
  },
  commonContainerStyles: {
    paddingTop: "0px !important",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 9999,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    // justifyContent: "center",
    // overflow: "hidden", // Hide any potential overflow
    padding: theme.spacing(2), // Add some padding to the content
  },
  image: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain", // Ensure the image fits within its container
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  position: "absolute",
  top: 25,
  right: 20,
  ".MuiBadge-dot": {
    width: 20,
    height: 20,
    border: `2px solid ${theme.palette.background.paper}`,
    background: "#1d428a",
    padding: "0 4px",
  },
  ".MuiBadge-badge": {
    padding: "5px",
    height: 35,
    borderRadius: "30px",
  },
}));

const StyledRoundIcon = styled("div")({
  borderRadius: "50%", // Make the icon round
  overflow: "hidden",
  width: "100%",
  height: "100%",
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AnthrosUploadFile = ({ playerData, setAnthrosData }) => {
  const classes = useStyles();
  //..getting context keys
  const { user, selectedEventId } = useContext(AppDataContext);
  let userAppRoles = user.appRoles || [];

  //... File Upload data holders.
  const [loading, setLoading] = useState(false);
  const [currentPreview, setCurrentPreview] = useState({
    id: null,
    file: null,
    details: null,
  });
  const [displayFileName, setDisplayFileName] = useState(null);
  const [finalFileName, setFinalFileName] = useState(null);
  const [source, setSource] = useState(null);
  const [isUploadingInProgress, setIsUploadingInProgress] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [showFileUploadProgressBar, setShowFileUploadProgressBar] =
    useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPreviewImageDialog, setOpenPreviewImageDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  //... Alert data holders.
  const [alert, setAlert] = useState(false);
  const [alertType, setAlertType] = useState("warning");
  const [alertMessage, setAlertMessage] = useState("");

  //... File refs data holders.
  const elementFileRef = useRef(null);
  const selectedFileRef = useRef(null);

  const fileStructureRef = useRef({
    fileName: null,
    thumbnailFileName: null,
  });

  //... File Allowed extention  data holders.
  const allowedFileExtensions = [
    "png",
    "jpeg",
    "jpg",
    "heic",
    "webm",
    "mp4",
    "mov",
  ];

  //...Image file extentions
  const validComparableImgExt = ["png", "jpeg", "jpg", "heic"];
  const validDownloadableImgExt = ["heic"];

  //... Handlers..
  const uploadDocumentChangeHandler = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      //... Reset the current preview
      setCurrentPreview({
        id: null,
        file: null,
        details: null,
      });

      const file = e.target.files[0];
      let blob = file.slice(0, file.size, file.type);
      let extension = file.name.substring(file.name.lastIndexOf(".") + 1);

      //... Extention Validation
      if (!allowedFileExtensions.includes(extension.toLowerCase())) {
        setAlert(true);
        setAlertType("warning");
        setAlertMessage(
          `Please select files with one of ${allowedFileExtensions.join(
            ", "
          )} formats.`
        );
        clearFileInput();
        return;
      }
      //... Set Display filename.
      setDisplayFileName(file.name.replace(`.${extension}`, ""));

      //... Set new filename.
      let newFileName = uuidv1() + "." + extension;

      const newFile = new File([blob], newFileName, { type: file.type });
      setFinalFileName(newFile);
      fileStructureRef.current = {
        ...fileStructureRef.current,
        fileName: newFileName,
      };
    }
  };

  const uploadDocumentHandler = (file) => {
    if (!file) {
      setAlert(true);
      setAlertType("warning");
      setAlertMessage("Please select file for upload.");
      return false;
    }

    (async () => {
      if (file) {
        try {
          setIsUploadingInProgress(true);
          const sourceType = getSourceType(file?.name);

          if (sourceType === "video") {
            await extractThumbnail(file);
          }
          //... Upload blob to azure storage
          setShowFileUploadProgressBar(true);

          //... Get metadata from current process.
          const metadata = getMetaInformation();
          await uploadVideoToBlobWithProgress(file, setProgressValue, metadata);
          await saveFileDetailsToDB();

          setAlertMessage("File saved successfully.");
          setAlertType("success");
          setAlert(true);
        } catch (e) {
          console.log("error", e);
          setAlertMessage("File saving failed.");
          setAlertType("error");
          setAlert(true);
        } finally {
          setShowFileUploadProgressBar(false);
          setIsUploadingInProgress(false);
        }
      }
    })();
  };

  //... Utility to delete saved file.
  const deleteMediaFromDB = async () => {
    try {
      setLoading(true);
      const result = await PlayerProfileService.deletePlayerMediaDetails(
        playerData?.eventId,
        playerData.playerID,
        user.loginID,
        selectedFileRef.current
      );

      if (result) {
        const filterAttempt = playerData?.videoCapture?.filter(
          (data) => data.attempt !== selectedFileRef?.current?.attempt
        );
        setAnthrosData((prevData) => ({
          ...prevData,
          videoCapture: filterAttempt,
        }));
        setLoading(false);
        setAlertProps("success", "Media deleted successfully.");
      }
    } catch (error) {
      setAlertProps("error", "Error while deleting media.");
      console.error("Error while deleting media", error);
    }
  };

  const handleDeleteMedia = (fileDetails) => {
    selectedFileRef.current = fileDetails;
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteDialogAgree = () => {
    setOpenDeleteDialog(false);
    deleteMediaFromDB();
  };

  const handleFullPreview = ({ fileName }) => {
    if (!fileName) {
      setAlertProps("error", "Error while getting media information.");
      return;
    }
    const url = Video_baseUrl(fileName);
    setOpenPreviewImageDialog(true);
    setImageUrl(url);
  };

  const handleClosePreviewImageDialog = () => {
    setOpenPreviewImageDialog(false);
  };

  //... Utility to show notification alert.
  const setAlertProps = (type, message) => {
    setAlert(true);
    setAlertType(type);
    setAlertMessage(message);
  };

  //... Utility to save file details to DB.
  const saveFileDetailsToDB = async () => {
    const metadata = getMetaInformation();

    // Use reduce function to find the maximum attempt number
    const maxAttempt = playerData?.videoCapture?.reduce(
      (max, video) => Math.max(max, video.attempt),
      0
    );

    const newAttempt = {
      attempt: (maxAttempt || 0) + 1,
      metaDataID: 0,
      fileName: fileStructureRef?.current?.fileName,
      stationId: 100,
      utcStartDate: new Date(),
      utcEndDate: new Date(),
      playerName: playerData.playerName.replace(/'/g, '"') + "",
      jsonData: JSON.stringify(metadata),
      userBy: user.loginID,
      isModified: true,
      angleType: 0,
    };

    try {
      setLoading(true);
      await PlayerProfileService.insertPlayerMediaDetails([
        {
          playerID: playerData?.playerID,
          eventId: playerData?.eventId,
          videoCapture: [newAttempt],
          videoAngles: [],
        },
      ]);

      //... Update states holders.
      setAnthrosData((prevplayerData) => ({
        ...prevplayerData,
        videoCapture: [...prevplayerData.videoCapture, newAttempt],
      }));
      clearFileInput();
    } catch (e) {
      console.log("error", e);
    } finally {
      setLoading(false);
    }
  };

  //... Utility to get current meta information.
  const getMetaInformation = () => {
    // Use reduce function to find the maximum attempt number
    const maxAttempt = playerData?.videoCapture?.reduce(
      (max, video) => Math.max(max, video.attempt),
      0
    );
    return {
      playerName: playerData.playerName.replace(/'/g, '"'),
      attemptId: (maxAttempt || 0) + 1 + "",
      playerID: playerData.playerID.toString(),
      thumnailName: fileStructureRef?.current?.thumbnailFileName || "",
      displayFileName,
    };
  };

  //... Utility to extract tumbnail file as blob.
  async function extractThumbnail(file) {
    try {
      const snapshoter = new VideoSnapshot(file);
      const previewSrc = await snapshoter.takeSnapshot(3);

      let newFileName = uuidv1() + ".png";
      fileStructureRef.current = {
        ...fileStructureRef.current,
        thumbnailFileName: newFileName,
      };

      fetch(previewSrc)
        .then((res) => res.blob())
        .then((blob) => {
          const thumbnailFile = new File([blob], newFileName, {
            type: "image/png",
          });
          uploadThumbnailFile(thumbnailFile);
        });
    } catch (e) {
      console.log("Error - extractThumbnail", e);
    }
  }

  //... Utility to upload tumbnail file as blob.
  async function uploadThumbnailFile(file) {
    try {
      await uploadAnthrosFileToBlob(file, {});
    } catch (e) {
      console.log("Error - uploadThumbnailFile", e);
    }
  }

  //... Utility to clear selected file input.
  const clearFileInput = () => {
    elementFileRef.current.value = null;
    setFinalFileName(null);
    setDisplayFileName(null);
  };

  //... Utility to format time.
  const getFormattedTime = (time) => {
    return (
      new Date(time).toLocaleDateString("en-US", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      }) +
      " " +
      new Date(time).toLocaleTimeString("en-US")
    );
  };

  //... Utility to get source type of file.
  const getSourceType = (file) => {
    const extension = file.substring(file.lastIndexOf(".") + 1);
    return validComparableImgExt?.includes(extension.toLowerCase())
      ? "image"
      : "video";
  };

  //... Utility to preview selected file.
  const previewHandler = (fileDetails, playId) => {
    const extension = fileDetails?.fileName?.substring(
      fileDetails?.fileName?.lastIndexOf(".") + 1
    );
    if (validDownloadableImgExt.includes(extension.toLowerCase())) {
      downloadHeicFile(fileDetails?.fileName);
      return;
    }

    setCurrentPreview({
      id: playId,
      file: fileDetails?.fileName,
      details: fileDetails,
    });
    setSource(getSourceType(fileDetails?.fileName));
  };

  //... Utility to dowload Heic files.
  const downloadHeicFile = (filename) => {
    const link = document.createElement("a");
    link.href = Video_baseUrl(filename);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //... Render utilities.
  const RenderContentStreamPanel = () => {
    return (
      <Box className={classes.contentStreamStyles}>
        {currentPreview?.file ? (
          source === "video" ? (
            <video
              controls
              autoPlay
              src={Video_baseUrl(currentPreview?.file)}
              className={classes.videoMediaStyles}
              width="100%"
              height="100%"
            ></video>
          ) : (
            <ImageMagnifier
              imageUrl={
                source === "image"
                  ? Video_baseUrl(currentPreview?.file)
                  : placeholder
              }
            />
          )
        ) : (
          <img
            alt="no-video-selected"
            src={placeholder}
            className={classes.mediaStyles}
          />
        )}
      </Box>
    );
  };

  //..Utility to validate value
  const validateValue = useCallback((value) => {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }

    if (typeof value === "number") {
      return value > 0 ? value : 0;
    }

    return "--"; // Default return when the value doesn't meet the conditions
  }, []);

  //..Utility to get source title..
  const getPreviewTitle = (file) => {
    const extension = (
      file?.substring(file?.lastIndexOf(".") + 1) || ""
    ).toLowerCase();

    return validDownloadableImgExt.includes(extension) ? "Download" : "Preview";
  };

  //... Utility to dowload resource open it to the new tab.
  const openFileInNewTab = async (url, fileName) => {
    await fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const fileUrl = window.URL.createObjectURL(blob);
        const newTab = window.open(fileUrl, "_blank");

        // Optional: Set the download attribute for compatibility
        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = fileName;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(fileUrl);
      });
  };

  return (
    <Box>
      {loading && (
        <div className={classes.overlay}>
          <CircularProgress />
        </div>
      )}

      <Grid container spacing={2}>
        <Grid item lg={12} className={`${classes.commonContainerStyles}`}>
          <Alert severity="info" sx={{ padding: "2px 16px" }}>
            Please note that only the following file extensions are allowed:{" "}
            {allowedFileExtensions.join(", ")}.
          </Alert>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ position: "relative" }} my={0.6}>
        <Grid
          item
          lg={4}
          sm={12}
          className={`${classes.commonContainerStyles}`}
        >
          <Paper elevation={3} sx={{ my: 2 }}>
            <Box sx={{ width: "100%", height: "100%" }}>
              <Box p={2}>
                <RenderContentStreamPanel />
              </Box>

              {/* Upload Section only for Admin role */}
              {userAppRoles.includes(Role.Admin) ? (
                <Paper elevation={3} sx={{ my: 0.6 }}>
                  <Box p={2}>
                    <input
                      type="file"
                      onChange={uploadDocumentChangeHandler}
                      className={classes.fileInput}
                      ref={elementFileRef}
                      // accept="image/png,image/jpeg,application/pdf,.xlsx,.doc,.docx,.txt"
                    />
                    <Divider sx={{ marginTop: "1rem", marginBottom: "1rem" }} />
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="space-between"
                    >
                      <Box style={{ marginRight: "8px" }}>
                        <Button
                          variant="contained"
                          component="label"
                          onClick={() => uploadDocumentHandler(finalFileName)}
                          startIcon={<CloudUploadIcon />}
                          size="small"
                          disabled={isUploadingInProgress}
                        >
                          Upload
                        </Button>{" "}
                        <Button
                          variant="contained"
                          component="label"
                          onClick={() => clearFileInput()}
                          startIcon={<ClearIcon />}
                          size="small"
                          color="error"
                        >
                          Clear
                        </Button>
                      </Box>
                      {showFileUploadProgressBar && (
                        <Box display="flex" ml={0.5} alignItems="center">
                          <Box>
                            <Typography
                              variant="caption"
                              color="green"
                              mr={1}
                              fontWeight={600}
                            >
                              {progressValue < 100
                                ? `Uploading...`
                                : `Saving...`}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              position: "relative",
                              display: "inline-flex",
                            }}
                          >
                            <Progressbar progressVal={progressValue} />
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Paper>
              ) : null}

              {/* Record details container all user role except admin */}
              {!userAppRoles.includes(Role.Admin) ? (
                <Paper elevation={3} sx={{ my: 0.6 }}>
                  <Box p={2}>
                    <CardHeader
                      avatar={
                        <Avatar sx={{ bgcolor: red[500] }} aria-label="Avatar">
                          <InterpreterModeIcon />
                        </Avatar>
                      }
                      title={`${validateValue(playerData?.playerName)}`}
                      subheader={`${
                        currentPreview?.details?.utcStartDate
                          ? getFormattedTime(
                              currentPreview?.details?.utcStartDate
                            )
                          : "--"
                      }`}
                    />
                  </Box>
                </Paper>
              ) : null}
            </Box>
          </Paper>
        </Grid>

        <Grid
          item
          lg={8}
          sm={12}
          className={`${classes.commonContainerStyles}`}
        >
          <Box style={{ width: "100%" }} sx={{ my: 2 }}>
            <Grid container spacing={2}>
              {playerData?.videoCapture?.map((row, index) => {
                const badgeContent =
                  getSourceType(row?.fileName) === "video" ? (
                    <StyledRoundIcon>
                      <VideocamIcon />
                    </StyledRoundIcon>
                  ) : (
                    <StyledRoundIcon>
                      <ImageIcon />
                    </StyledRoundIcon>
                  );

                const badgeColor =
                  getSourceType(row?.fileName) === "video"
                    ? "secondary"
                    : "primary";

                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={6}
                    lg={2.4}
                    key={`ivc_${index}`}
                  >
                    <Card
                      sx={{
                        position: "relative",
                        cursor: "pointer",
                        border:
                          currentPreview?.id === index
                            ? "2px solid rgb(175, 24, 45)"
                            : "none",
                        borderRadius: 1,
                        minHeight: "265px",
                      }}
                      elevation={2}
                    >
                      <Box onClick={() => previewHandler(row, index)}>
                        <TitleTooltip
                          title={
                            getSourceType(row?.fileName) === "video"
                              ? getPreviewTitle(
                                  JSON.parse(row?.jsonData)?.thumnailName
                                ) || ""
                              : getPreviewTitle(row?.fileName)
                          }
                          placement="top-start"
                          key={index}
                        >
                          <CardMedia
                            component="img"
                            width="100%"
                            height="120"
                            image={
                              getSourceType(row?.fileName) === "video"
                                ? Video_baseUrl(
                                    JSON.parse(row?.jsonData)?.thumnailName
                                  ) || ""
                                : Video_baseUrl(row?.fileName)
                            }
                            // onClick={() => previewHandler(row, index)}
                            onError={(e) => {
                              e.target.src = novideoIcon; // Set the URL of your default image
                            }}
                          />
                        </TitleTooltip>
                        <StyledBadge
                          badgeContent={badgeContent}
                          color={badgeColor}
                        />
                        <CardContent>
                          <TitleTooltip
                            title={
                              (row?.jsonData &&
                                JSON.parse(row.jsonData)?.displayFileName) ||
                              row?.playerName
                            }
                            placement="bottom"
                            key={index}
                          >
                            <Typography
                              gutterBottom
                              variant="subtitle2"
                              component="div"
                              noWrap
                            >
                              {(row?.jsonData &&
                                JSON.parse(row.jsonData)?.displayFileName) ||
                                row?.playerName}
                            </Typography>
                          </TitleTooltip>
                          <Typography variant="caption" color="text.secondary">
                            {getFormattedTime(row.utcStartDate)}
                          </Typography>
                        </CardContent>
                      </Box>
                      <CardActions
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "end",
                        }}
                      >
                        <Divider width="50%" />
                        <Stack direction="row" spacing={1}>
                          {/* Full Preview Image Section */}
                          {getSourceType(row?.fileName) === "image" ? (
                            <TitleTooltip
                              title="Full Preview"
                              placement="top-start"
                              key={index}
                            >
                              <IconButton
                                aria-label="Full Preview"
                                color="primary"
                                onClick={() => handleFullPreview(row)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </TitleTooltip>
                          ) : null}
                          {userAppRoles.includes(Role.Admin) ? (
                            <TitleTooltip
                              title="Delete file"
                              placement="top-start"
                              key={index}
                            >
                              <IconButton
                                aria-label="delete interview"
                                color="secondary"
                                onClick={() => handleDeleteMedia(row)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TitleTooltip>
                          ) : null}
                        </Stack>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Snackbar
        open={alert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        onClose={() => setAlert(false)}
      >
        <Alert
          onClose={() => setAlert(false)}
          severity={alertType}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Document Delete Dialog Box */}
      <Dialog
        open={openDeleteDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleDeleteDialogClose}
        aria-describedby="alert-delete-dialog-slide-description"
        maxWidth="lg"
        PaperProps={{
          style: {
            width: "40%",
          },
        }}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            Do you wish to delete?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteDialogClose}
            variant="contained"
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDialogAgree}
            variant="contained"
            color="primary"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPreviewImageDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClosePreviewImageDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Preview Image
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            color="secondary"
            onClick={handleClosePreviewImageDialog}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent className={classes.dialogContent}>
          <Box sx={{ padding: "1rem" }}>
            <img src={imageUrl} alt="Large Image" className={classes.image} />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AnthrosUploadFile;
